import { useHomepageUpcomingQuery } from '@/generated/graphql';
import {
  CircularProgress,
  Stack,
  StackProps,
  styled,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DateTime } from 'luxon';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';
import { useExperienceCategory } from '../../../../hooks/use-experience-category';
/**
 * Sample Data
 */

const sampleData = [
  {
    id: '1',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/1.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-01T10:00:00.000Z',
    title: 'Sample Experience 1',
    host: {
      profile: {
        nickname: 'Host1',
      },
    },
    content: {
      description: 'Description for Sample Experience 1',
    },
  },
  {
    id: '2',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/2.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-02T11:00:00.000Z',
    title: 'Sample Experience 2',
    host: {
      profile: {
        nickname: 'Host2',
      },
    },
    content: {
      description: 'Description for Sample Experience 2',
    },
  },
  {
    id: '3',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/3.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-03T12:00:00.000Z',
    title: 'Sample Experience 3',
    host: {
      profile: {
        nickname: 'Host3',
      },
    },
    content: {
      description: 'Description for Sample Experience 3',
    },
  },
  {
    id: '4',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/4.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-04T13:00:00.000Z',
    title: 'Sample Experience 4',
    host: {
      profile: {
        nickname: 'Host4',
      },
    },
    content: {
      description: 'Description for Sample Experience 4',
    },
  },
  {
    id: '5',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/5.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-05T14:00:00.000Z',
    title: 'Sample Experience 5',
    host: {
      profile: {
        nickname: 'Host5',
      },
    },
    content: {
      description: 'Description for Sample Experience 5',
    },
  },
  {
    id: '1',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/1.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-01T10:00:00.000Z',
    title: 'Sample Experience 1',
    host: {
      profile: {
        nickname: 'Host1',
      },
    },
    content: {
      description: 'Description for Sample Experience 1',
    },
  },
  {
    id: '2',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/2.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-02T11:00:00.000Z',
    title: 'Sample Experience 2',
    host: {
      profile: {
        nickname: 'Host2',
      },
    },
    content: {
      description: 'Description for Sample Experience 2',
    },
  },
  {
    id: '3',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/3.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-03T12:00:00.000Z',
    title: 'Sample Experience 3',
    host: {
      profile: {
        nickname: 'Host3',
      },
    },
    content: {
      description: 'Description for Sample Experience 3',
    },
  },
  {
    id: '4',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/4.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-04T13:00:00.000Z',
    title: 'Sample Experience 4',
    host: {
      profile: {
        nickname: 'Host4',
      },
    },
    content: {
      description: 'Description for Sample Experience 4',
    },
  },
  {
    id: '5',
    images: {
      edges: [
        {
          node: {
            file: {
              url: 'https://files.curiko-staging.ca/seed/5.jpg',
            },
          },
        },
      ],
    },
    nextEvent: '2023-10-05T14:00:00.000Z',
    title: 'Sample Experience 5',
    host: {
      profile: {
        nickname: 'Host5',
      },
    },
    content: {
      description: 'Description for Sample Experience 5',
    },
  },
];

/**
 * END Sample Data
 */

const CardLink = styled(Link)`
  text-decoration: none;
  border-radius: 20px;
  outline: 2px solid transparent;
  transition: outline 0.5s;

  &:hover,
  &:focus {
    outline: 2px solid ${({ theme }) => theme.palette.common.homepageVeryLightPurple};
    outline-offset: 4px;
  }
`;

const CardWrapper = styled((props: StackProps) => (
  <Stack {...props} p={2} gap={2} direction="row" />
))`
  border-radius: 20px;
  background-color: ${({ theme }) => theme.palette.common.homepagePurple};
`;

const CardTitle = styled(Typography)`
  color: ${({ theme }) => theme.palette.common.yellow};
  font-weight: 700;
  font-size: 16px;
`;

const CardImage = styled(Image)`
  width: 100px; /* Match the desired width */
  height: 100px; /* Match the desired height */
  min-width: 100px; /* Prevent shrinking in flex context if needed */
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${({ theme }) => theme.palette.common.homepageVeryLightPurple};
  border-right: 4px solid ${({ theme }) => theme.palette.common.homepageVeryLightPurple};
  border-left: 3px solid ${({ theme }) => theme.palette.common.homepageVeryLightPurple};
  border-bottom: 1px solid ${({ theme }) => theme.palette.common.homepageVeryLightPurple};
`;

const HostedBy = styled(Typography)`
  font-style: italic;
  color: rgba(255, 255, 255, 0.7);
`;

export function Upcoming() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { category, closeModal, tab, setTab } = useExperienceCategory();
  const now = useMemo(() => {
    return DateTime.now().startOf('hour').minus({ minute: 15 }).toISO();
  }, []);
  const { data, loading } = useHomepageUpcomingQuery({ variables: { now } });
  const experiences = (data?.experiences?.edges || []).filter(
    experience => experience.node.category === category,
  );

  return (
    <Stack gap={2} padding={2} alignItems="stretch" justifyContent="center">
      {loading && <CircularProgress sx={{ color: theme => theme.palette.common.yellow, my: 2 }} />}
      {experiences.map(({ node: experience }) => {
        const mainImageUrl = experience.images?.edges?.[0]?.node?.file?.url!;
        const nextDate = DateTime.fromISO(experience.nextEvent || DateTime.now().toISO());
        const description = (
          <Typography color="white" sx={{ width: '100%', mt: { xs: 2, sm: 0 } }}>
            {experience.content?.description}
          </Typography>
        );
        return (
          <CardLink
            href={`/experiences/${experience.id}`}
            key={experience.id}
            style={{ textDecoration: 'none' }}
          >
            <CardWrapper sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
              <Stack direction="row" gap={2}>
                <CardImage src={mainImageUrl} alt="" width={200} height={200} priority={false} />
                <Stack gap={1}>
                  <CardTitle>{experience.title}</CardTitle>
                  <HostedBy>Hosted by {experience.host?.profile?.nickname}</HostedBy>
                  <Typography color="white">
                    {nextDate.toLocaleString(DateTime.DATETIME_MED)}
                  </Typography>
                  {!isMobile && description}
                </Stack>
              </Stack>
              {isMobile && description}
            </CardWrapper>
          </CardLink>
        );
      })}
    </Stack>
  );
}
